import type { ChangeEvent } from 'react';
import { useGetSpaceAISettings } from '../../../inbox/api/useGetSpaceAISettings';
import { useUpdateSpaceAISettings } from '../../../inbox/api/useUpdateSpaceAISettings';

type Props = {
  spaceId: string;
};

type AiFeature = {
  id: string;
  title: string;
  description: string;
  /** Agreement of the state label: « Activé » (l'assistant) vs « Activée » (la lecture). */
  gender: 'masculine' | 'feminine';
};

/** Everything the space-wide switch turns on or off. */
export const AI_FEATURES: AiFeature[] = [
  {
    id: 'assistant',
    title: 'Assistant IA',
    description: 'Questions sur la copropriété et historique des conversations.',
    gender: 'masculine',
  },
  {
    id: 'documents',
    title: 'Lecture des documents',
    description: 'Extraction du texte des PDF, photos et Google Docs pour l’assistant.',
    gender: 'feminine',
  },
  {
    id: 'inbox',
    title: 'Analyse des e-mails',
    description: 'Résumé, devis, rendez-vous et prestataires suggérés dans la boîte de réception.',
    gender: 'feminine',
  },
];

export function getFeatureStateLabel(feature: AiFeature, isAiEnabled: boolean): string {
  const label = isAiEnabled ? 'Activé' : 'Désactivé';
  return feature.gender === 'feminine' ? `${label}e` : label;
}

export function useAiSettingsCard(props: Props) {
  const { data: isAiEnabled = true, isLoading } = useGetSpaceAISettings({ spaceId: props.spaceId });
  const { mutate: updateAISettings, isPending: isUpdating } = useUpdateSpaceAISettings();

  function handleToggle(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    updateAISettings({ spaceId: props.spaceId, isAiEnabled: checked });
  }

  return {
    isAiEnabled,
    isDisabled: isLoading || isUpdating,
    statusLabel: isAiEnabled ? 'Activée' : 'Désactivée',
    featureStateColor: isAiEnabled ? ('done' as const) : ('default' as const),
    getFeatureStateLabel: (feature: AiFeature) => getFeatureStateLabel(feature, isAiEnabled),
    dataNotice: isAiEnabled
      ? 'Les contenus analysés (questions, documents, e-mails) sont envoyés à Google Gemini. L’IA ne peut modifier aucune donnée.'
      : 'Aucun contenu n’est envoyé à l’IA. Les documents en attente seront lus à la réactivation ; l’historique de l’assistant est conservé 30 jours.',
    handleToggle,
  };
}
